import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Search,
  Filter,
  Eye,
  EyeOff,
  Flag,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Clock,
  DollarSign,
  Users,
  PieChart,
  ArrowUpDown,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import adminPortfolioService from "@/services/api/admin.portfolio.service";

const AdminHoldingsDashboard = () => {
  // State management
  const [holdings, setHoldings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedHoldings, setSelectedHoldings] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [selectedHoldingDetail, setSelectedHoldingDetail] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    field: "createdAt",
    direction: "desc",
  });

  // Filter states
  const [filters, setFilters] = useState({
    search: "",
    portfolioType: "",
    isRead: "",
    minValue: "",
    maxValue: "",
    userId: "",
    portfolioId: "",
  });

  // Pagination - Fixed to not cause infinite re-renders
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  // Fetch holdings data - FIXED VERSION
  const fetchHoldings = useCallback(
    async (overrideParams = {}) => {
      try {
        setLoading(true);

        const queryParams = {
          ...filters,
          sortBy: sortConfig.field,
          sortOrder: sortConfig.direction,
          page: pagination.page,
          limit: pagination.limit,
          ...overrideParams, // This will override the above values
        };

        // Remove empty filters
        Object.keys(queryParams).forEach((key) => {
          if (
            queryParams[key] === "" ||
            queryParams[key] === null ||
            queryParams[key] === undefined
          ) {
            delete queryParams[key];
          }
        });



        const response = await adminPortfolioService.getAllHoldings(
          queryParams
        );

        if (response.success) {
          setHoldings(response.data.holdings);
          setPagination(response.data.pagination);
          setStats(response.data.summary);
        }
      } catch (error) {
        console.error("Error fetching holdings:", error);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [filters, sortConfig, pagination.page, pagination.limit] // Include pagination in dependencies
  );

  // Fetch holdings stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await adminPortfolioService.getHoldingsStats();
      if (response.success) {
        setStats(response.data.overview);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchHoldings();
    fetchStats();
  }, []); // Only run once on mount

  useEffect(() => {
    fetchHoldings();
  }, [fetchHoldings]);

  // Refresh data
  const handleRefresh = () => {
    setRefreshing(true);
    fetchHoldings();
    fetchStats();
  };
  const handleViewDetails = async (holdingId) => {
    try {
      setDetailLoading(true);
      setShowDetailModal(true);

      const response = await adminPortfolioService.getHoldingDetails(holdingId);
      if (response.success) {
        setSelectedHoldingDetail(response.data);
      }
    } catch (error) {
      console.error("Error fetching holding details:", error);
    } finally {
      setDetailLoading(false);
    }
  };
  // Handle sorting
  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
    // Reset to first page and fetch immediately
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchHoldings({ page: 1 });
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    // Don't auto-fetch on filter change, wait for apply
  };
  // Apply filters
  const applyFilters = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchHoldings({ page: 1 });
    setShowFilters(false);
  };

  // Clear filters
  const clearFilters = () => {
    const clearedFilters = {
      search: "",
      portfolioType: "",
      isRead: "",
      minValue: "",
      maxValue: "",
      userId: "",
      portfolioId: "",
    };
    setFilters(clearedFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchHoldings({ ...clearedFilters, page: 1 });
  };

  const handlePageChange = (newPage) => {

    // Update pagination state and fetch immediately with the new page
    setPagination((prev) => ({ ...prev, page: newPage }));
    // Fetch with the new page immediately to avoid race condition
    fetchHoldings({ page: newPage });
  };

  // Handle selection
  const handleSelectHolding = (holdingId) => {
    setSelectedHoldings((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(holdingId)) {
        newSelected.delete(holdingId);
      } else {
        newSelected.add(holdingId);
      }
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    if (selectedHoldings.size === holdings.length) {
      setSelectedHoldings(new Set());
    } else {
      setSelectedHoldings(new Set(holdings.map((h) => h._id)));
    }
  };

  // Mark as read/unread
  const handleMarkAsRead = async (holdingIds, isRead = true) => {
    try {
      if (holdingIds.length === 1) {
        await adminPortfolioService.markHoldingAsRead(holdingIds[0], isRead);
      } else {
        await adminPortfolioService.markMultipleHoldingsAsRead(
          holdingIds,
          isRead
        );
      }
      fetchHoldings(); // Refresh current page
      setSelectedHoldings(new Set());
    } catch (error) {
      console.error("Error marking holdings:", error);
    }
  };

  // Batch operations
  const handleBatchOperation = async (operation) => {
    try {
      const holdingIds = Array.from(selectedHoldings);
      await adminPortfolioService.performBatchOperation({
        holdingIds,
        operation,
      });
      fetchHoldings(); // Refresh current page
      setSelectedHoldings(new Set());
    } catch (error) {
      console.error("Error performing batch operation:", error);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Format percentage
  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(2)}%`;
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      ACTIVE: "text-green-600 bg-green-50",
      INACTIVE: "text-red-600 bg-red-50",
      PENDING: "text-yellow-600 bg-yellow-50",
      APPROVED: "text-green-600 bg-green-50",
      REJECTED: "text-red-600 bg-red-50",
    };
    return colors[status] || "text-gray-600 bg-gray-50";
  };

  // Stats cards data
  const statsCards = useMemo(
    () => [
      {
        title: "Total Holdings",
        value: stats?.totalHoldings?.toLocaleString() || "0",
        icon: PieChart,
        color: "text-blue-600 bg-blue-50",
      },
      {
        title: "Total Value",
        value: formatCurrency(stats?.totalValue),
        icon: DollarSign,
        color: "text-green-600 bg-green-50",
      },
      {
        title: "Unread Holdings",
        value: stats?.unreadCount?.toLocaleString() || "0",
        icon: Eye,
        color: "text-orange-600 bg-orange-50",
      },
      {
        title: "Average Value",
        value: formatCurrency(stats?.avgHoldingValue),
        icon: TrendingUp,
        color: "text-purple-600 bg-purple-50",
      },
    ],
    [stats]
  );

  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field)
      return <ArrowUpDown className="w-4 h-4 opacity-50" />;
    return sortConfig.direction === "asc" ? (
      <ChevronUp className="w-4 h-4 text-blue-600" />
    ) : (
      <ChevronDown className="w-4 h-4 text-blue-600" />
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Holdings Management
              </h1>
              <p className="text-gray-600 mt-1">
                Monitor and manage all portfolio holdings
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw
                  className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
                />
                Refresh
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors"
              >
                <Filter className="w-4 h-4" />
                Filters
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statsCards.map((stat, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    {stat.title}
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {stat.value}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <stat.icon className="w-6 h-6" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
              <button
                onClick={() => setShowFilters(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search
                </label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  placeholder="Search holdings..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Portfolio Type
                </label>
                <select
                  value={filters.portfolioType}
                  onChange={(e) =>
                    handleFilterChange("portfolioType", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Types</option>
                  <option value="MUTUAL FUND">Mutual Fund</option>
                  <option value="EQUITY">Equity</option>
                  <option value="BONDS">Bonds</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Read Status
                </label>
                <select
                  value={filters.isRead}
                  onChange={(e) => handleFilterChange("isRead", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All</option>
                  <option value="true">Read</option>
                  <option value="false">Unread</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Min Value
                </label>
                <input
                  type="number"
                  value={filters.minValue}
                  onChange={(e) =>
                    handleFilterChange("minValue", e.target.value)
                  }
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Clear All
              </button>
              <button
                onClick={applyFilters}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}

        {/* Bulk Actions */}
        {selectedHoldings.size > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <span className="text-sm font-medium text-gray-700">
                {selectedHoldings.size} holdings selected
              </span>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() =>
                    handleMarkAsRead(Array.from(selectedHoldings), true)
                  }
                  className="flex items-center gap-2 px-3 py-2 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg transition-colors text-sm"
                >
                  <CheckCircle className="w-4 h-4" />
                  Mark as Read
                </button>
                <button
                  onClick={() =>
                    handleMarkAsRead(Array.from(selectedHoldings), false)
                  }
                  className="flex items-center gap-2 px-3 py-2 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-lg transition-colors text-sm"
                >
                  <EyeOff className="w-4 h-4" />
                  Mark as Unread
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Holdings Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative min-h-[400px]">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-12 px-6 py-4">
                    <input
                      type="checkbox"
                      checked={
                        selectedHoldings.size === holdings.length &&
                        holdings.length > 0
                      }
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-4 text-left">
                    <button
                      onClick={() => handleSort("portfolio.portfolioName")}
                      className="flex items-center gap-2 font-semibold text-gray-900 hover:text-blue-600 transition-colors"
                    >
                      Portfolio
                      <SortIcon field="portfolio.portfolioName" />
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left">
                    <button
                      onClick={() => handleSort("productDetails.name")}
                      className="flex items-center gap-2 font-semibold text-gray-900 hover:text-blue-600 transition-colors"
                    >
                      Product
                      <SortIcon field="productDetails.name" />
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left">
                    <button
                      onClick={() => handleSort("quantity")}
                      className="flex items-center gap-2 font-semibold text-gray-900 hover:text-blue-600 transition-colors"
                    >
                      Quantity
                      <SortIcon field="quantity" />
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left">
                    <button
                      onClick={() => handleSort("currentValuation.totalValue")}
                      className="flex items-center gap-2 font-semibold text-gray-900 hover:text-blue-600 transition-colors"
                    >
                      Value
                      <SortIcon field="currentValuation.totalValue" />
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left">
                    <button
                      onClick={() => handleSort("performance.unrealizedProfit")}
                      className="flex items-center gap-2 font-semibold text-gray-900 hover:text-blue-600 transition-colors"
                    >
                      P&L
                      <SortIcon field="performance.unrealizedProfit" />
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left">User</th>
                  <th className="px-6 py-4 text-left">Status</th>
                  <th className="px-6 py-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading && holdings.length === 0 ? (
                  // Initial loading skeleton - match the limit for stable height
                  [...Array(pagination.limit || 10)].map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-6 py-4">
                        <div className="w-4 h-4 bg-gray-200 rounded"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="w-32 h-4 bg-gray-200 rounded"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="w-40 h-4 bg-gray-200 rounded"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="w-16 h-4 bg-gray-200 rounded"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="w-24 h-4 bg-gray-200 rounded"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="w-20 h-4 bg-gray-200 rounded"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="w-28 h-4 bg-gray-200 rounded"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="w-16 h-4 bg-gray-200 rounded"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="w-8 h-4 bg-gray-200 rounded"></div>
                      </td>
                    </tr>
                  ))
                ) : holdings.length === 0 ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="px-6 py-12 text-center text-gray-500"
                    >
                      No holdings found
                    </td>
                  </tr>
                ) : (
                  holdings.map((holding) => (
                    <tr
                      key={holding._id}
                      className={`hover:bg-gray-50 transition-colors ${!holding.adminRead ? "bg-blue-50/30" : ""
                        }`}
                    >
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedHoldings.has(holding._id)}
                          onChange={() => handleSelectHolding(holding._id)}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">
                            {holding.portfolio?.portfolioName || "N/A"}
                          </div>
                          <div className="text-sm text-gray-500">
                            {holding.portfolio?.portfolioType || "N/A"}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">
                            {holding.productDetails?.name || "N/A"}
                          </div>
                          <div className="text-sm text-gray-500">
                            {holding.productDetails?.fundHouse ||
                              holding.productDetails?.sector ||
                              "N/A"}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-medium text-gray-900">
                          {holding.quantity?.toLocaleString() || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-medium text-gray-900">
                          {formatCurrency(holding.currentValuation?.totalValue)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className={`flex items-center gap-1 ${(holding.performance?.unrealizedProfit || 0) >= 0
                            ? "text-green-600"
                            : "text-red-600"
                            }`}
                        >
                          {(holding.performance?.unrealizedProfit || 0) >= 0 ? (
                            <TrendingUp className="w-4 h-4" />
                          ) : (
                            <TrendingDown className="w-4 h-4" />
                          )}
                          <span className="font-medium">
                            {formatCurrency(
                              holding.performance?.unrealizedProfit
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">
                            {holding.user?.fullName || "Unknown User"}
                          </div>
                          <div className="text-sm text-gray-500">
                            {holding.user?.email || "N/A"}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                              holding.portfolio?.status
                            )}`}
                          >
                            {holding.adminRead ? (
                              <CheckCircle className="w-3 h-3" />
                            ) : (
                              <Clock className="w-3 h-3" />
                            )}
                            {holding.adminRead ? "Read" : "Unread"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleMarkAsRead([holding._id], !holding.adminRead)}
                            className={`p-2 rounded-lg transition-colors ${holding.adminRead
                              ? "text-green-600 hover:bg-green-50 bg-green-50"
                              : "text-orange-600 hover:bg-orange-50 bg-orange-50"
                              }`}
                            title={holding.adminRead ? "Mark as unread" : "Mark as read"}
                          >
                            {holding.adminRead ? (
                              <CheckCircle className="w-4 h-4" />
                            ) : (
                              <Clock className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleViewDetails(holding._id)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {/* Subtle loading indicator for pagination transitions */}
            {loading && holdings.length > 0 && (
              <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px] flex items-center justify-center z-10">
                <div className="flex flex-col items-center gap-2">
                  <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
                  <p className="text-sm font-medium text-blue-600">Updating...</p>
                </div>
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-700 text-center sm:text-left">
                Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{" "}
                <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span>{" "}
                of <span className="font-medium">{pagination.total}</span> results
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="p-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 transition-all flex items-center"
                  title="Previous Page"
                >
                  <ChevronLeft className="w-5 h-5" />
                  <span className="hidden sm:inline ml-1">Previous</span>
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                    .filter((pageNum) => {
                      if (pagination.pages <= 5) return true;
                      if (pageNum === 1 || pageNum === pagination.pages) return true;
                      // Show current page and neighbors
                      return Math.abs(pageNum - pagination.page) <= (window.innerWidth < 640 ? 0 : 1);
                    })
                    .map((pageNum, index, array) => (
                      <React.Fragment key={pageNum}>
                        {index > 0 && array[index - 1] !== pageNum - 1 && (
                          <span className="px-1 text-gray-400">...</span>
                        )}
                        <button
                          onClick={() => handlePageChange(pageNum)}
                          className={`min-w-[32px] sm:min-w-[40px] h-8 sm:h-10 text-xs sm:text-sm font-medium rounded-lg transition-all ${pagination.page === pageNum
                            ? "bg-blue-600 text-white shadow-sm"
                            : "text-gray-600 hover:bg-gray-100"
                            }`}
                        >
                          {pageNum}
                        </button>
                      </React.Fragment>
                    ))}
                </div>

                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="p-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 transition-all flex items-center"
                  title="Next Page"
                >
                  <span className="hidden sm:inline mr-1">Next</span>
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Holding Detail Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">
                Holding Details
              </h2>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedHoldingDetail(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {detailLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-gray-600">Loading details...</span>
                </div>
              ) : selectedHoldingDetail ? (
                <div className="space-y-6">
                  {/* User Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">
                      User Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Name
                        </label>
                        <p className="text-gray-900">
                          {selectedHoldingDetail.user?.fullName || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Email
                        </label>
                        <p className="text-gray-900">
                          {selectedHoldingDetail.user?.email || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Mobile
                        </label>
                        <p className="text-gray-900">
                          {selectedHoldingDetail.user?.mobileNo || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          KYC Status
                        </label>
                        <p
                          className={`${selectedHoldingDetail.user?.isKycVerified
                            ? "text-green-600"
                            : "text-red-600"
                            }`}
                        >
                          {selectedHoldingDetail.user?.isKycVerified
                            ? "Verified"
                            : "Not Verified"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Portfolio Information */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">
                      Portfolio Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Portfolio Name
                        </label>
                        <p className="text-gray-900">
                          {selectedHoldingDetail.portfolio?.portfolioName ||
                            "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Type
                        </label>
                        <p className="text-gray-900">
                          {selectedHoldingDetail.portfolio?.portfolioType ||
                            "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Risk Profile
                        </label>
                        <p className="text-gray-900">
                          {selectedHoldingDetail.portfolio?.riskProfile ||
                            "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Total Value
                        </label>
                        <p className="text-gray-900">
                          {formatCurrency(
                            selectedHoldingDetail.portfolio?.totalValue
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Holding Details */}
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">
                      Holding Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Product Name
                        </label>
                        <p className="text-gray-900">
                          {selectedHoldingDetail.product?.name ||
                            selectedHoldingDetail.mutualFund?.name ||
                            "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Quantity
                        </label>
                        <p className="text-gray-900">
                          {selectedHoldingDetail.quantity?.toLocaleString() ||
                            0}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          Current Value
                        </label>
                        <p className="text-gray-900">
                          {formatCurrency(
                            selectedHoldingDetail.currentValuation?.totalValue
                          )}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">
                          P&L
                        </label>
                        <p
                          className={`${(selectedHoldingDetail.performance
                            ?.unrealizedProfit || 0) >= 0
                            ? "text-green-600"
                            : "text-red-600"
                            }`}
                        >
                          {formatCurrency(
                            selectedHoldingDetail.performance?.unrealizedProfit
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">
                  No details available
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHoldingsDashboard;
